import { useState, useEffect, useMemo, useCallback } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  Search,
  ChevronLeft,
  ChevronRight,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  AlertTriangle,
  Eye,
  SlidersHorizontal,
  X,
  Flag,
} from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useLanguage } from "@/hooks/use-language";
import { getQuestions, getCategories } from "@/lib/api";
import { getToken } from "@/lib/auth";
import { createErrorMessage } from "@/lib/error-message";
import { getQuestionText, getChoiceText, getExplanation } from "@/lib/localize";
import type { Question, Category, PaginationMeta } from "@/lib/types";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Markdown } from "@/components/ui/markdown";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const Route = createFileRoute("/_main/exams/$slug/admin/questions/")({
  component: AdminQuestionsPage,
});

const API_BASE =
  import.meta.env.VITE_API_URL || "http://localhost:8080/api/v1";

function adminFetch(endpoint: string, options: RequestInit = {}) {
  const token = getToken();
  if (!token) {
    return Promise.reject(new Error("로그인이 필요합니다"));
  }
  return fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...((options.headers as Record<string, string>) ?? {}),
    },
  });
}

function AdminQuestionsPage() {
  const { slug: examSlug } = Route.useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { t, locale } = useLanguage();

  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState<string | undefined>(undefined);
  const [categoryId, setCategoryId] = useState<number | undefined>(undefined);
  const [fromNumber, setFromNumber] = useState("");
  const [toNumber, setToNumber] = useState("");
  const [isMultiple, setIsMultiple] = useState<string>("all");
  const [hasExplanation, setHasExplanation] = useState<string>("all");
  const [showFilters, setShowFilters] = useState(false);

  const [questions, setQuestions] = useState<Question[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<Question | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [previewTarget, setPreviewTarget] = useState<Question | null>(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);

  const perPage = 20;

  // Admin guard
  useEffect(() => {
    if (!authLoading && (!user || user.role !== "admin")) {
      navigate({ to: "/dashboard", replace: true });
    }
  }, [user, authLoading, navigate]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput || undefined);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Fetch categories
  useEffect(() => {
    if (!examSlug) return;
    getCategories(examSlug)
      .then(setCategories)
      .catch(() => {});
  }, [examSlug]);

  // Fetch questions
  const filters = useMemo(() => ({
    fromNumber: fromNumber ? Number(fromNumber) : undefined,
    toNumber: toNumber ? Number(toNumber) : undefined,
    isMultiple: isMultiple === "all" ? undefined : isMultiple === "true",
    hasExplanation: hasExplanation === "all" ? undefined : hasExplanation === "true",
  }), [fromNumber, toNumber, isMultiple, hasExplanation]);

  const fetchQuestions = useCallback(() => {
    if (!examSlug) return;
    setLoading(true);
    setError(null);
    getQuestions(examSlug, page, perPage, categoryId, search, filters)
      .then((result) => {
        setQuestions(result.questions);
        setMeta(result.meta);
      })
      .catch((err: unknown) => {
        setError(createErrorMessage(err, "문제 목록을 불러올 수 없습니다"));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [examSlug, page, perPage, categoryId, search, filters]);

  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  const totalPages = meta?.total_pages ?? 1;

  const categoryMap = useMemo(() => {
    const map = new Map<number, string>();
    for (const cat of categories) {
      map.set(cat.id, cat.name);
    }
    return map;
  }, [categories]);

  function getCategoryName(q: Question): string {
    if (q.category) return q.category.name;
    if (q.category_id && categoryMap.has(q.category_id)) {
      return categoryMap.get(q.category_id)!;
    }
    return "-";
  }

  function handleCategoryChange(value: string | null) {
    if (!value || value === "all") {
      setCategoryId(undefined);
    } else {
      setCategoryId(Number(value));
    }
    setPage(1);
  }

  function openPreviewDialog(question: Question) {
    setPreviewTarget(question);
    setPreviewDialogOpen(true);
  }

  function openDeleteDialog(question: Question) {
    setDeleteTarget(question);
    setDeleteDialogOpen(true);
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await adminFetch(`/admin/exams/${examSlug}/questions/${deleteTarget.id}`, {
        method: "DELETE",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error || "삭제에 실패했습니다");
      }
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      fetchQuestions();
    } catch (err) {
      alert(createErrorMessage(err, "삭제에 실패했습니다"));
    } finally {
      setDeleting(false);
    }
  }

  if (authLoading || (!user || user.role !== "admin")) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-zinc-900 dark:text-zinc-50 sm:text-3xl">
              {t("admin.questions_title")}
            </h1>
            <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
              {t("admin.questions_desc")}
            </p>
          </div>
          <div className="flex gap-2">
            <Link
              to="/exams/$slug/admin/reports"
              params={{ slug: examSlug }}
            >
              <Button size="lg" variant="outline">
                <Flag className="mr-1.5 h-4 w-4 text-red-500" />
                {t("report.admin_title")}
              </Button>
            </Link>
            <Link
              to="/exams/$slug/admin/questions/bulk"
              params={{ slug: examSlug }}
            >
              <Button size="lg" variant="outline">
                <Plus className="mr-1.5 h-4 w-4" />
                {t("admin.bulk_create")}
              </Button>
            </Link>
            <Link
              to="/exams/$slug/admin/questions/new"
              params={{ slug: examSlug }}
            >
              <Button size="lg">
                <Plus className="mr-1.5 h-4 w-4" />
                {t("admin.new_question")}
              </Button>
            </Link>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6 space-y-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
              <Input
                placeholder={t("admin.search_placeholder")}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select
              value={categoryId ? String(categoryId) : "all"}
              onValueChange={handleCategoryChange}
            >
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue placeholder={t("admin.select_category")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("admin.all_categories")}</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={String(cat.id)}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant={showFilters ? "default" : "outline"}
              size="sm"
              className="h-9 shrink-0"
              onClick={() => setShowFilters((v) => !v)}
            >
              <SlidersHorizontal className="mr-1.5 h-4 w-4" />
              {t("common.filter")}
              {(fromNumber || toNumber || isMultiple !== "all" || hasExplanation !== "all") && (
                <Badge variant="secondary" className="ml-1.5 text-[10px] px-1.5 py-0">
                  {t("common.on")}
                </Badge>
              )}
            </Button>
          </div>

          {showFilters && (
            <div className="flex flex-col gap-3 rounded-lg border border-zinc-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900 sm:flex-row sm:items-end">
              <div className="flex flex-1 items-end gap-2">
                <div className="flex-1 space-y-1">
                  <label className="text-xs font-medium text-zinc-500">{t("admin.number_from")}</label>
                  <Input
                    type="number"
                    placeholder={t("admin.number_from_placeholder")}
                    value={fromNumber}
                    onChange={(e) => { setFromNumber(e.target.value); setPage(1); }}
                    className="h-9"
                  />
                </div>
                <span className="pb-2 text-zinc-400">~</span>
                <div className="flex-1 space-y-1">
                  <label className="text-xs font-medium text-zinc-500">{t("admin.number_to")}</label>
                  <Input
                    type="number"
                    placeholder={t("admin.number_to_placeholder")}
                    value={toNumber}
                    onChange={(e) => { setToNumber(e.target.value); setPage(1); }}
                    className="h-9"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-zinc-500">{t("admin.answer_type")}</label>
                <Select value={isMultiple} onValueChange={(v) => { setIsMultiple(v ?? "all"); setPage(1); }}>
                  <SelectTrigger className="h-9 w-full sm:w-[130px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("common.all")}</SelectItem>
                    <SelectItem value="false">{t("admin.single_answer")}</SelectItem>
                    <SelectItem value="true">{t("admin.multiple_answer")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-zinc-500">{t("common.explanation")}</label>
                <Select value={hasExplanation} onValueChange={(v) => { setHasExplanation(v ?? "all"); setPage(1); }}>
                  <SelectTrigger className="h-9 w-full sm:w-[130px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("common.all")}</SelectItem>
                    <SelectItem value="true">{t("admin.has_explanation")}</SelectItem>
                    <SelectItem value="false">{t("admin.no_explanation")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-9 shrink-0 text-zinc-500"
                onClick={() => {
                  setFromNumber("");
                  setToNumber("");
                  setIsMultiple("all");
                  setHasExplanation("all");
                  setPage(1);
                }}
              >
                <X className="mr-1 h-3.5 w-3.5" />
                {t("common.reset")}
              </Button>
            </div>
          )}
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
            <span className="ml-2 text-zinc-500">{t("common.loading")}</span>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-center text-red-600 dark:border-red-800 dark:bg-red-950 dark:text-red-400">
            {t("common.error")}: {error}
          </div>
        )}

        {/* Empty */}
        {!loading && !error && questions.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 text-zinc-500">
            <AlertTriangle className="mb-4 h-12 w-12" />
            <p className="text-lg font-medium">{t("admin.no_questions")}</p>
            <p className="text-sm">{t("admin.no_questions_desc")}</p>
          </div>
        )}

        {/* Table */}
        {!loading && !error && questions.length > 0 && (
          <>
            {/* Mobile: card layout */}
            <div className="space-y-3 sm:hidden">
              {questions.map((q) => (
                <div
                  key={q.id}
                  className="rounded-lg border border-zinc-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900"
                >
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-sm font-semibold text-zinc-900 dark:text-zinc-50">
                      #{q.question_number}
                    </span>
                    <div className="flex items-center gap-1">
                      {q.is_multiple && (
                        <Badge variant="secondary" className="text-xs">
                          {t("common.multiple")}
                        </Badge>
                      )}
                      <Badge variant="outline" className="text-xs">
                        {getCategoryName(q)}
                      </Badge>
                    </div>
                  </div>
                  <p className="mb-3 line-clamp-2 text-sm text-zinc-600 dark:text-zinc-400">
                    {getQuestionText(q, locale)}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => openPreviewDialog(q)}
                    >
                      <Eye className="mr-1 h-3 w-3" />
                      {t("common.preview")}
                    </Button>
                    <Link
                      to="/exams/$slug/admin/questions/$id/edit"
                      params={{ slug: examSlug, id: String(q.id) }}
                      className="flex-1"
                    >
                      <Button variant="outline" size="sm" className="w-full">
                        <Pencil className="mr-1 h-3 w-3" />
                        {t("common.edit")}
                      </Button>
                    </Link>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => openDeleteDialog(q)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop: table layout */}
            <div className="hidden overflow-x-auto rounded-lg border border-zinc-200 bg-white dark:border-zinc-700 dark:bg-zinc-900 sm:block">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-200 bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-800">
                    <th className="px-4 py-3 text-left font-medium text-zinc-600 dark:text-zinc-300">
                      #
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-zinc-600 dark:text-zinc-300">
                      {t("admin.question_text")}
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-zinc-600 dark:text-zinc-300">
                      {t("common.category")}
                    </th>
                    <th className="px-4 py-3 text-center font-medium text-zinc-600 dark:text-zinc-300">
                      {t("admin.is_multiple")}
                    </th>
                    <th className="px-4 py-3 text-right font-medium text-zinc-600 dark:text-zinc-300">
                      {t("common.action")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {questions.map((q) => (
                    <tr
                      key={q.id}
                      className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50 dark:border-zinc-800 dark:hover:bg-zinc-800/50"
                    >
                      <td className="px-4 py-3 font-medium text-zinc-900 dark:text-zinc-50">
                        {q.question_number}
                      </td>
                      <td className="max-w-xs px-4 py-3 text-zinc-700 dark:text-zinc-300">
                        <span className="line-clamp-1">{getQuestionText(q, locale)}</span>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className="text-xs">
                          {getCategoryName(q)}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {q.is_multiple ? (
                          <Badge variant="secondary" className="text-xs">
                            {t("common.multiple")}
                          </Badge>
                        ) : (
                          <span className="text-zinc-400">{t("common.single")}</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openPreviewDialog(q)}
                          >
                            <Eye className="mr-1 h-3 w-3" />
                            {t("common.preview")}
                          </Button>
                          <Link
                            to="/exams/$slug/admin/questions/$id/edit"
                            params={{ slug: examSlug, id: String(q.id) }}
                          >
                            <Button variant="outline" size="sm">
                              <Pencil className="mr-1 h-3 w-3" />
                              {t("common.edit")}
                            </Button>
                          </Link>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => openDeleteDialog(q)}
                          >
                            <Trash2 className="mr-1 h-3 w-3" />
                            {t("common.delete")}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="mt-6 flex items-center justify-center gap-4">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <ChevronLeft className="mr-1 h-4 w-4" />
                {t("common.previous")}
              </Button>
              <span className="text-sm text-zinc-600 dark:text-zinc-400">
                {page} / {totalPages} {t("common.page")}
                {meta && (
                  <span className="ml-2 text-zinc-400">
                    ({t("common.total")} {meta.total}{t("common.count_suffix")})
                  </span>
                )}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                {t("common.next")}
                <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          </>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("admin.delete_confirm")}</DialogTitle>
            <DialogDescription>
              {t("questions.question_num")}{deleteTarget?.question_number}{t("admin.delete_confirm_desc")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose
              render={<Button variant="outline" />}
            >
              {t("common.cancel")}
            </DialogClose>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
              {t("common.delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {t("questions.question_num")}{previewTarget?.question_number}
              {previewTarget?.is_multiple && (
                <Badge variant="secondary" className="text-xs">{t("common.multiple_choice")}</Badge>
              )}
              <Badge variant="outline" className="text-xs">
                {previewTarget ? getCategoryName(previewTarget) : ""}
              </Badge>
            </DialogTitle>
          </DialogHeader>

          {previewTarget && (
            <div className="space-y-4">
              {/* Question text */}
              <div>
                <p className="mb-1 text-xs font-semibold text-zinc-500">{t("admin.text_ko_label")}</p>
                <div className="rounded-lg border bg-muted/30 p-3">
                  <Markdown>{previewTarget.text_ko}</Markdown>
                </div>
              </div>

              {previewTarget.text_en && (
                <div>
                  <p className="mb-1 text-xs font-semibold text-zinc-500">{t("admin.text_en_label")}</p>
                  <div className="rounded-lg border bg-muted/30 p-3">
                    <Markdown>{previewTarget.text_en}</Markdown>
                  </div>
                </div>
              )}

              {/* Choices */}
              {previewTarget.choices && previewTarget.choices.length > 0 && (
                <div>
                  <p className="mb-2 text-xs font-semibold text-zinc-500">{t("common.choices")}</p>
                  <div className="space-y-2">
                    {previewTarget.choices.map((c) => (
                      <div
                        key={c.id}
                        className={`flex items-start gap-2 rounded-lg border p-2.5 text-sm ${
                          c.is_correct
                            ? "border-green-300 bg-green-50 dark:border-green-700 dark:bg-green-950"
                            : "border-zinc-200 dark:border-zinc-700"
                        }`}
                      >
                        <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                          c.is_correct
                            ? "bg-green-500 text-white"
                            : "bg-zinc-200 text-zinc-600 dark:bg-zinc-700 dark:text-zinc-400"
                        }`}>
                          {c.label}
                        </span>
                        <span className="pt-0.5">{getChoiceText(c, locale)}</span>
                        {c.is_correct && (
                          <Badge className="ml-auto shrink-0 bg-green-600 text-xs text-white">{t("common.correct")}</Badge>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Explanation */}
              {getExplanation(previewTarget, locale) && (
                <div>
                  <p className="mb-1 text-xs font-semibold text-zinc-500">{t("common.explanation")}</p>
                  <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-950">
                    <Markdown>{getExplanation(previewTarget, locale)!}</Markdown>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Link
              to="/exams/$slug/admin/questions/$id/edit"
              params={{ slug: examSlug, id: String(previewTarget?.id ?? "") }}
            >
              <Button variant="outline" size="sm">
                <Pencil className="mr-1 h-3 w-3" />
                {t("common.edit")}
              </Button>
            </Link>
            <DialogClose render={<Button size="sm" />}>
              {t("common.confirm")}
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
